import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { Observable, from, defer, EMPTY } from "rxjs"
import { switchMap, catchError, filter } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import { compact, flatten } from "lodash"
import { Router } from "../../../server/router"
import type { EpicDependencies } from "../../rxjs/configureEpics"
import {
    ASSIGNMENT_FORM_SUBMITTED,
    ASSIGNMENTS_AGENDA_MOUNTED,
    ASSIGNMENTS_REORDERED,
    CLOSE_ASSIGNMENT_FORM_CLICKED,
    DELETE_ASSIGNMENT_CLICKED,
    fetchAssignmentsFailure,
    fetchAssignmentsSuccess,
} from "./actions"
import { composeAssignment, getHighestSortIndex } from "./utils"
import {
    ASSIGNMENT_CREATED,
    ASSIGNMENT_DELETED,
    ASSIGNMENT_UPDATED,
    assignmentCreated,
    assignmentDeleted,
    assignmentUpdated,
} from "../../data/actions"
import {
    getAssignmentById,
    getAssignmentsBySelectedLocationId,
    getDataForAssignmentsTable,
    shouldDownloadAssignmentsData,
} from "./selectors"
import { getHttpHeaders } from "../../auth/selectors"
import { addAssignmentRequest, deleteAssignmentRequest, fetchAssignments, updateAssignmentRequest } from "./request"
import { sendOperationsFailure, sendOperationsRequested, sendOperationsSuccess } from "../../operations/actions"
import { ACCOUNT_SELECTED, COOKIE_LOADED, REFETCH_DATA } from "../../auth/actions"
import { createLoadingEnded, createLoadingStarted } from "../../filters/actions"
import { LOCATION_INITIALIZED, LOCATION_SELECTED } from "../../common/locationsPicker/actions"
import type { Assignment } from "../../../graphql/types"
import { LOCATION_AGENDA_MOUNTED } from "../locations/actions"
import { getSelectedLocationId } from "../../common/locationsPicker/selectors"
import { logger } from "../../../utils/loggerWrap"
import { Action, StateObject } from "../../redux/types"

export const assignmentsLoadingStartedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(
            ASSIGNMENTS_AGENDA_MOUNTED,
            ACCOUNT_SELECTED,
            COOKIE_LOADED,
            REFETCH_DATA,
            LOCATION_INITIALIZED,
            LOCATION_SELECTED,
            LOCATION_AGENDA_MOUNTED
        ),
        filter(() => shouldDownloadAssignmentsData(deps.getState())),
        filter(() => !!getSelectedLocationId(deps.getState())),
        switchMap(() => [createLoadingStarted("assignments")])
    )

export const fetchAssignmentsRequested = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(
            ASSIGNMENTS_AGENDA_MOUNTED,
            ACCOUNT_SELECTED,
            COOKIE_LOADED,
            REFETCH_DATA,
            LOCATION_INITIALIZED,
            LOCATION_SELECTED,
            LOCATION_AGENDA_MOUNTED
        ),
        filter(() => shouldDownloadAssignmentsData(deps.getState())),
        filter(() => !!getSelectedLocationId(deps.getState())),
        switchMap(() =>
            defer(() => fetchAssignments(getHttpHeaders(deps.getState()), null)).pipe(
                switchMap((response: Maybe<any>) => {
                    if (response) {
                        return [createLoadingEnded("assignments"), fetchAssignmentsSuccess(response)]
                    } else {
                        return [
                            createLoadingEnded("assignments"),
                            fetchAssignmentsFailure({
                                status: 404,
                            }),
                        ]
                    }
                }),
                catchError(error => from([fetchAssignmentsFailure(error), createLoadingEnded("assignments")]))
            )
        )
    )

export const assignmentFormSubmittedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ASSIGNMENT_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("assignmentFormSubmittedEpics - form submitted", {
                data: action.payload,
            })
            const ass = getAssignmentById(deps.getState(), action.payload.newValue.assignmentId)
            const sortIndex = !action.payload.oldValue
                ? getHighestSortIndex(
                      getAssignmentsBySelectedLocationId(deps.getState()),
                      action.payload.newValue.parentId
                  ) + 1
                : ass
                  ? ass.sortIndex
                  : null
            const assignment = composeAssignment(action.payload.newValue, sortIndex, deps.idgen)
            return action.payload.oldValue
                ? [assignmentUpdated(assignment), sendOperationsRequested([])]
                : // @ts-expect-error
                  [assignmentCreated(assignment), sendOperationsRequested([])]
        })
    )

export const addAssignmentRequestedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ASSIGNMENT_CREATED),
        switchMap((action: Action) =>
            defer(() => addAssignmentRequest(getHttpHeaders(deps.getState()), action.payload.assignment)).pipe(
                switchMap(() => {
                    // TODO add response validation -> if (response)....
                    return [sendOperationsSuccess([])]
                }),
                catchError(error => from([sendOperationsFailure(error)]))
            )
        )
    )

export const updateAssignmentRequestedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ASSIGNMENT_UPDATED),
        switchMap((action: Action) =>
            defer(() => updateAssignmentRequest(getHttpHeaders(deps.getState()), action.payload.newValue)).pipe(
                switchMap(() => {
                    return [sendOperationsSuccess([])]
                }),
                catchError(error => from([sendOperationsFailure(error)]))
            )
        )
    )

export const deleteAssignmentRequestedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ASSIGNMENT_DELETED),
        switchMap((action: Action) =>
            defer(() =>
                deleteAssignmentRequest(
                    getHttpHeaders(deps.getState()),
                    action.payload.assignmentId,
                    action.payload.locationId
                )
            ).pipe(
                switchMap(() => {
                    return [sendOperationsSuccess([])]
                }),
                catchError(error => from([sendOperationsFailure(error)]))
            )
        )
    )

export const deleteAssignmentClickedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(DELETE_ASSIGNMENT_CLICKED),
        switchMap((action: Action) => {
            logger.info("deleteAssignmentClickedEpics - form submitted", {
                data: action.payload,
            })
            const ass = getAssignmentById(deps.getState(), action.payload.assignmentId)
            const children = ass && ass.children ? ass.children.map(a => assignmentDeleted(a.id, ass?.locationId)) : []
            return [assignmentDeleted(action.payload.assignmentId, ass?.locationId), ...children]
        })
    )

export const assignmentSubmittedRouterEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ASSIGNMENT_FORM_SUBMITTED, CLOSE_ASSIGNMENT_FORM_CLICKED, DELETE_ASSIGNMENT_CLICKED),
        switchMap(() => {
            Router.push("/assignments")
            return EMPTY
        })
    )

export const assignmentsReorderedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ASSIGNMENTS_REORDERED),
        switchMap((action: Action) => {
            logger.info("assignmentsReorderedEpics", {
                data: action.payload,
            })

            const newData: Assignment[] = action.payload.newData
            const flattenedNewData = flatten(
                newData.map(el => (el.children ? [{ ...el, children: null }, ...el.children] : [el]))
            )
            const oldData: Assignment[] = getDataForAssignmentsTable(deps.getState())
            const flattenedOldData = flatten(
                oldData.map(el => (el.children ? [{ ...el, children: null }, ...el.children] : [el]))
            )
            const actions = flattenedOldData.map(el => {
                const newEl = flattenedNewData.find(nEl => nEl.id === el.id)

                if (!newEl) {
                    return null
                }

                const diff = newEl.sortIndex !== el.sortIndex || newEl.parentId !== el.parentId

                if (diff) {
                    return assignmentUpdated({
                        id: el.id,
                        parentId: newEl.parentId,
                        sortIndex: newEl.sortIndex,
                        name: newEl.name,
                        locationId: newEl.locationId,
                        config: el.config,
                        showAsQuickFilter: newEl.showAsQuickFilter === undefined ? false : newEl.showAsQuickFilter,
                    })
                } else {
                    return null
                }
            })
            return compact(actions)
        })
    )

export default [
    assignmentsLoadingStartedEpics,
    fetchAssignmentsRequested,
    deleteAssignmentRequestedEpic,
    addAssignmentRequestedEpic,
    updateAssignmentRequestedEpic,
    assignmentsReorderedEpics,
    assignmentSubmittedRouterEpics,
    assignmentFormSubmittedEpics,
    deleteAssignmentClickedEpics,
]
